import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import LoadingOverlay from './components/ui/LoadingOverlay';

import { ThemeProvider } from './context/ThemeContext';
import { AccountDataProvider } from './context/AccountContext';
import { FavoritesProvider } from './context/FavoritesContext';
import { CartProvider } from './context/CartContext';
import AppShell from './components/AppShell';
import ScrollManager from './components/ScrollManager';
import RequireAuth from './components/auth/RequireAuth';
import { OMNIVERSAIL_NEW_PAGES } from './config/featureFlags';
import OmniversailGlobalJsonLd from './pages/omniversail/OmniversailGlobalJsonLd';
import AdmissionAccessGate from './pages/omniversail/AdmissionAccessGate';

const LanguageLearningPage = lazy(() =>
  import('./pages/Services/Academics/LanguageLearningPage'),
);
const Login = lazy(() => import('./pages/Account/Login'));
const Register = lazy(() => import('./pages/Account/Register'));
const ConfirmEmail = lazy(() => import('./pages/Account/ConfirmEmail'));
const ForgotPassword = lazy(() => import('./pages/Account/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/Account/ResetPassword'));
const SocialCallback = lazy(() => import('./pages/Account/SocialCallback'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Checkout = lazy(() => import('./pages/Checkout'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const Account = lazy(() => import('./pages/Account/Account'));
const OmniversailShowcase = lazy(() => import('./pages/omniversail/OmniversailShowcase'));
const CoursesLandingPage = lazy(() => import('./pages/omniversail/CoursesLandingPage'));
const CollegesLandingPage = lazy(() => import('./pages/omniversail/CollegesLandingPage'));
const CompetitionsMirrorPage = lazy(() => import('./pages/omniversail/CompetitionsMirrorPage'));
const CurriculumSubjectPage = lazy(() => import('./pages/omniversail/CurriculumSubjectPage'));
const PrepNotesPage = lazy(() => import('./pages/omniversail/PrepNotesPage'));
const LawExamNotes = lazy(() => import('./pages/omniversail/LawExamNotes'));
const VocabDecksPage = lazy(() => import('./pages/omniversail/VocabDecksPage'));
const AdmissionConsultation = lazy(() => import('./pages/omniversail/AdmissionConsultation'));
const AdmissionIntentContract = lazy(() => import('./pages/omniversail/AdmissionIntentContract'));
const IntentLetterView = lazy(() => import('./pages/omniversail/IntentLetterView'));
const QuestionBankApp = lazy(() => import('./features/questionBank/components/QuestionBankApp'));
const Careers = lazy(() => import('./pages/Careers'));
const JobApplication = lazy(() => import('./pages/JobApplication'));
const OmniversailAbout = lazy(() => import('./pages/omniversail/About'));
const Contact = lazy(() => import('./pages/Contact'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));

function RouteFallback() {
  // Shown while a lazy route chunk loads (i.e. on page switches). Dim the page
  // and spin so navigation always gives visible feedback.
  return <LoadingOverlay label="Loading…" />;
}

/**
 * The Language Center is open to everyone (free / unregistered included) so
 * visitors can browse languages, open drills and play audio without being bounced
 * to a login wall. Signed-in users still get personalised sync via AccountContext;
 * the upstream "members-only" guard was removed to stop the jumpy redirect.
 */
function GuardedLanguageCenter() {
  return <LanguageLearningPage />;
}

/* Legacy /academics/language/:lang deep links → canonical /language/:lang.
   Preserve any ?view= (and other query) so deep-linked drill views survive. */
function LegacyLangRedirect() {
  const { lang } = useParams();
  const search = typeof window !== 'undefined' ? window.location.search : '';
  return <Navigate to={`/language/${lang || ''}${search || ''}`} replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AccountDataProvider>
        {/* Favorites + Cart read the account session, so they must sit inside
            AccountDataProvider. Both throw a clear error when used outside
            their provider — Register.js uses both, which is why the page used
            to crash without them here. */}
        <FavoritesProvider>
          <CartProvider>
            <BrowserRouter>
              <ScrollManager />
              {/* App-wide Organization + WebSite/SearchAction JSON-LD for GEO. */}
              <OmniversailGlobalJsonLd />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Public marketing home — rendered full-bleed (own nav and
                      footer), so it stays outside the app chrome. */}
                  <Route
                    path="/"
                    element={OMNIVERSAIL_NEW_PAGES ? <OmniversailShowcase /> : <LandingPage />}
                  />
                  <Route path="/checkout" element={<AppShell><Checkout /></AppShell>} />
                  <Route path="/payment-success" element={<AppShell><PaymentSuccess /></AppShell>} />
                  {/* Canonical language center route; legacy /academics/language
                      deep links (mgv.gallery review-cycle emails) redirect here. */}
                  <Route path="/language" element={<AppShell><GuardedLanguageCenter /></AppShell>} />
                  <Route path="/language/:lang" element={<AppShell><GuardedLanguageCenter /></AppShell>} />
                  <Route path="/academics/language" element={<Navigate to="/language" replace />} />
                  <Route path="/academics/language/:lang" element={<LegacyLangRedirect />} />
                  <Route path="/login" element={<AppShell><Login /></AppShell>} />
                  <Route path="/register" element={<AppShell><Register /></AppShell>} />
                  <Route path="/confirm-email" element={<AppShell><ConfirmEmail /></AppShell>} />
                  <Route path="/forgot-password" element={<AppShell><ForgotPassword /></AppShell>} />
                  <Route path="/reset-password" element={<AppShell><ResetPassword /></AppShell>} />
                  {/* Social sign-in (Google/WeChat) hands back a short-lived SSO token. */}
                  <Route path="/social-callback" element={<AppShell><SocialCallback /></AppShell>} />
                  {/* Profile / account page — guarded so anonymous visitors are
                      sent to login (which returns here on success). */}
                  <Route path="/account" element={<AppShell><RequireAuth><Account /></RequireAuth></AppShell>} />
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/curriculum" element={<CoursesLandingPage />} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/curriculum/:subject" element={<CurriculumSubjectPage />} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/colleges" element={<CollegesLandingPage />} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/comps" element={<CompetitionsMirrorPage />} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/comps/question-bank/:bankId" element={<AppShell><QuestionBankApp /></AppShell>} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/notes" element={<AppShell><PrepNotesPage /></AppShell>} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/notes/:programId" element={<AppShell><PrepNotesPage /></AppShell>} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/notes/law" element={<AppShell><LawExamNotes /></AppShell>} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/notes/law/:exam" element={<AppShell><LawExamNotes /></AppShell>} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/notes/law/:exam/:itemId" element={<AppShell><LawExamNotes /></AppShell>} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/vocab" element={<VocabDecksPage />} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/vocab/:book" element={<VocabDecksPage />} />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route
                      path="/admission-consultation"
                      element={
                        <AdmissionAccessGate>
                          <AdmissionConsultation />
                        </AdmissionAccessGate>
                      }
                    />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route
                      path="/admission-intent"
                      element={
                        <AdmissionAccessGate>
                          <AdmissionIntentContract />
                        </AdmissionAccessGate>
                      }
                    />
                  )}
                  {OMNIVERSAIL_NEW_PAGES && (
                    <Route path="/intent-letter/:id" element={<IntentLetterView />} />
                  )}
                  <Route path="/careers" element={<AppShell><Careers /></AppShell>} />
                  {/* Application form for a specific opening (and the general
                      introduction form). Wired to POST /api/auth/job-application. */}
                  <Route path="/careers/apply/:roleId" element={<AppShell><JobApplication /></AppShell>} />
                  <Route path="/careers/introduce" element={<AppShell><JobApplication /></AppShell>} />
                  <Route path="/about" element={<OmniversailAbout />} />
                  <Route path="/contact" element={<AppShell><Contact /></AppShell>} />
                  <Route path="/search" element={<AppShell><SearchResultsPage /></AppShell>} />
                  <Route path="*" element={<LandingPage />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </CartProvider>
        </FavoritesProvider>
      </AccountDataProvider>
    </ThemeProvider>
  );
}
